import React from 'react';
import { Section, Button, FadeIn, SEO, DecorativeBackground } from '../components/UI';
import { MessageCircle, Heart, Sparkles } from 'lucide-react';

export default function Sorgvejleder() {
  return (
    <div className="pt-20">
      <SEO 
        title="Sorgsamtaler" 
        description="Støtte og vejledning gennem sorg og livskriser. Find ro og værdighed i en svær tid med nærværende samtaler."
      />
      {/* Hero Section */}
      <Section className="bg-transparent pt-8 md:pt-12 pb-12 md:pb-24 relative overflow-hidden">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center relative z-10">
          <div className="lg:col-span-5">
            <FadeIn className="pt-12 md:pt-0">
              <span className="text-sorg-earth font-medium tracking-[0.2em] uppercase text-xs mb-6 block">Samtaler & Støtte</span>
              <h1 className="text-4xl md:text-7xl font-serif text-sorg-dark mb-8 leading-[1.1]">
                Sorgsamtaler
              </h1>
              <p className="text-xl text-sorg-dark/70 leading-relaxed mb-10">
                Sorg er ikke en sygdom, der skal behandles, men en livsomstændighed, der skal bæres. Jeg støtter dig i at finde vej gennem mørket med nærvær og omsorg.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button to="/kontakt" className="px-6 py-2.5 md:px-8 md:py-3">Aftal en samtale</Button>
                <Button to="#indhold" variant="secondary" className="px-6 py-2.5 md:px-8 md:py-3">Læs mere</Button>
              </div>
            </FadeIn>
          </div>
          
          <div className="lg:col-span-7">
            <FadeIn direction="left" delay={0.2}>
              <div className="relative">
                {/* Decorative background element for the image */}
                <div className="absolute -inset-4 bg-sorg-beige/20 rounded-[2rem] blur-2xl -z-10 transform rotate-2"></div>
                <div className="aspect-[4/3] md:aspect-[16/10] w-full overflow-hidden rounded-[2rem] soft-shadow-lg border border-white/50">
                  <img 
                    src="https://assets.auxodev.dk/assets/88d34a44-1e08-49cc-ba2b-c330133a247e/7e977c34-f073-4384-be83-bc4b9a78bdef.jpg" 
                    alt="Sorgsamtaler - Serent landskab" 
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-1000"
                  />
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-6 md:left-0 lg:-left-6 bg-white/80 backdrop-blur-md p-6 rounded-2xl soft-shadow border border-white/50 max-w-[200px] hidden md:block">
                  <p className="text-sm font-serif text-sorg-dark italic">"Et trygt rum til dine sværeste følelser."</p>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Content */}
      <Section id="indhold" glass className="mt-0">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 relative z-10">
          <div className="lg:col-span-2 space-y-8 text-sorg-dark/70 text-lg leading-relaxed">
            <FadeIn>
              <h2 className="text-3xl font-serif text-sorg-dark mb-6">Når livet føles uoverskueligt</h2>
              <p>
                Sorg opstår ikke kun ved dødsfald. Det kan være tabet af et helbred, en skilsmisse, en mistet drøm eller en stor livsforandring, der efterlader dig med en følelse af tomhed og meningsløshed.
              </p>
              <p>
                I sorgsamtalerne hos mig er der ingen rigtige eller forkerte måder at sørge på. Jeg tager udgangspunkt i lige præcis der, hvor du er. Sammen skaber vi et rum, hvor dine følelser kan få lov at fylde, og hvor vi langsomt finder vejen tilbage til en hverdag, der giver mening – med sorgen som en del af din historie.
              </p>
            </FadeIn>



            <FadeIn delay={0.3}>
              <h3 className="text-2xl font-serif text-sorg-dark mb-4">Hvad vi kan tale om:</h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  'Tabet af en nærtstående',
                  'Skilsmisse og brudte relationer',
                  'Eksistentiel ensomhed',
                  'Livskriser og store overgange',
                  'Håndtering af svære følelser',
                  'At finde mening i det meningsløse'
                ].map((item) => (
                  <li key={item} className="flex items-start space-x-3">
                    <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-sorg-earth flex-shrink-0"></div>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </FadeIn>
          </div>

          <div className="lg:col-span-1">
            <FadeIn direction="left" className="bg-sorg-beige/20 p-10 rounded-3xl border border-sorg-beige sticky top-32">
              <h3 className="text-2xl font-serif text-sorg-dark mb-6 text-center">Aftal en samtale</h3>
              <p className="text-sm text-sorg-dark/60 mb-8 text-center">
                Jeg tilbyder samtaler både fysisk i rolige omgivelser og online.
              </p>
              <div className="space-y-4">
                <Button to="/kontakt" className="w-full">Kontakt mig</Button>
                <p className="text-xs text-center text-sorg-dark/40 italic">
                  Alle henvendelser behandles med fuld fortrolighed og respekt.
                </p>
              </div>
            </FadeIn>
          </div>
        </div>
      </Section>

      {/* Trust Section */}
      <Section className="bg-sorg-sand/40 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 text-center">
            <FadeIn delay={0.1}>
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-sorg-earth mx-auto mb-6 soft-shadow">
                <MessageCircle size={28} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-xl mb-3">Nærvær</h4>
              <p className="text-sm text-sorg-dark/60">Jeg lytter uden at dømme og skaber et trygt rum for dine ord.</p>
            </FadeIn>
            <FadeIn delay={0.2}>
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-sorg-earth mx-auto mb-6 soft-shadow">
                <Heart size={28} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-xl mb-3">Omsorg</h4>
              <p className="text-sm text-sorg-dark/60">Din trivsel og værdighed er altid i centrum for vores møde.</p>
            </FadeIn>
            <FadeIn delay={0.3}>
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-sorg-earth mx-auto mb-6 soft-shadow">
                <Sparkles size={28} strokeWidth={1} />
              </div>
              <h4 className="font-serif text-xl mb-3">Værdighed</h4>
              <p className="text-sm text-sorg-dark/60">Jeg ærer din proces og de relationer, der betyder noget for dig.</p>
            </FadeIn>
          </div>
        </div>
      </Section>
    </div>
  );
}
